import React, { useEffect, useMemo, useRef, useState } from "react";
import { Routes, Route, Link, useNavigate, useParams } from "react-router-dom";
import { adminApi, absoluteAdminUrl, pdfUrl, ADMIN_BASE, getStoredToken } from "./api";
import { formatINR, parseNumber, shortDate } from "./format";
import { toast } from "sonner";
import {
  Plus,
  Search,
  Pencil,
  Trash2,
  X,
  FileText,
  Download,
  Share2,
  Copy,
  ArrowLeft,
  Sparkles,
  Loader2,
  Package,
  Eye,
  GripVertical,
  Save,
  Briefcase,
} from "lucide-react";
import ExportMenu from "./ExportMenu";
import { QuotationImportUpload, QuotationImportReview } from "./QuotationImport";

const STATUSES = ["draft", "sent", "accepted", "rejected", "expired", "revised", "converted"];
const QUOTE_CHIP = {
  draft: "ax-chip",
  sent: "ax-chip ax-chip-info",
  accepted: "ax-chip ax-chip-success",
  rejected: "ax-chip ax-chip-danger",
  expired: "ax-chip ax-chip-warn",
  revised: "ax-chip ax-chip-gold",
  converted: "ax-chip ax-chip-success",
};

export default function QuotationsModule() {
  return (
    <Routes>
      <Route index element={<QuotationsList />} />
      <Route path="import" element={<QuotationImportUpload />} />
      <Route path="import/:sid" element={<QuotationImportReview />} />
      <Route path="new" element={<QuotationBuilder />} />
      <Route path=":id" element={<QuotationBuilder />} />
    </Routes>
  );
}

function QuotationsList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const nav = useNavigate();

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await adminApi.get("/quotations");
      setItems(r.data?.quotations || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load quotations");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    refresh();
  }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return items.filter((x) => {
      if (statusFilter !== "all" && x.status !== statusFilter) return false;
      if (!term) return true;
      return (
        (x.client_name || "").toLowerCase().includes(term) ||
        (x.quote_number || "").toLowerCase().includes(term) ||
        (x.client_phone || "").toLowerCase().includes(term)
      );
    });
  }, [items, q, statusFilter]);

  const onDelete = async (it) => {
    if (!window.confirm(`Delete quotation ${it.quote_number || ""}?`)) return;
    try {
      await adminApi.delete(`/quotations/${it.id}`);
      toast.success("Quotation deleted");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="p-10 max-w-[1320px] mx-auto" data-testid="quotations-list">
      <header className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <FileText className="w-3.5 h-3.5" style={{ color: "var(--ax-gold)" }} />
            <span className="text-[10.5px] tracking-[0.32em] uppercase" style={{ color: "var(--ax-gold)" }}>
              Quotations
            </span>
          </div>
          <h1 className="ax-serif text-5xl tracking-[-0.02em]">Cinematic proposals</h1>
          <p className="text-sm mt-3" style={{ color: "var(--ax-text-dim)" }}>
            Build room-wise, AI-described, GST-inclusive quotations for HNI clients.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="ax-btn ax-btn-ghost"
            onClick={() => nav("import")}
            data-testid="quote-import"
            title="Import a competitor quotation using AI"
          >
            <Sparkles className="w-4 h-4" /> Import existing
          </button>
          <button type="button" className="ax-btn ax-btn-gold" onClick={() => nav("new")} data-testid="quote-new">
            <Plus className="w-4 h-4" /> New quotation
          </button>
        </div>
      </header>

      <div className="mb-5 flex items-center justify-end">
        <ExportMenu entity="quotations" testidPrefix="quote-export" />
      </div>

      <div className="ax-glass mb-5 p-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg flex-1 min-w-[240px]" style={{ background: "rgba(255,255,255,0.02)", border: "1px solid var(--ax-border)" }}>
          <Search className="w-4 h-4" style={{ color: "var(--ax-text-muted)" }} />
          <input className="bg-transparent flex-1 outline-none text-sm" placeholder="Search client or quote #" value={q} onChange={(e) => setQ(e.target.value)} data-testid="quote-search" />
        </div>
        <select className="ax-select" style={{ width: 180 }} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="quote-filter-status">
          <option value="all">All statuses</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <div className="text-xs" style={{ color: "var(--ax-text-muted)" }}>
          {filtered.length} / {items.length}
        </div>
      </div>

      <div className="ax-glass overflow-hidden">
        <div className="ax-scroll overflow-x-auto">
          <table className="ax-table">
            <thead>
              <tr>
                <th>Quote #</th>
                <th>Client</th>
                <th>Project</th>
                <th>Status</th>
                <th>Grand total</th>
                <th>Updated</th>
                <th style={{ width: 140 }}></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="text-center py-10 text-sm" style={{ color: "var(--ax-text-muted)" }}>Loading…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={7} className="text-center py-10 text-sm" style={{ color: "var(--ax-text-muted)" }}>No quotations yet.</td></tr>
              )}
              {!loading && filtered.map((it) => (
                <tr key={it.id} data-testid={`quote-row-${it.id}`}>
                  <td className="ax-mono text-xs" style={{ color: "var(--ax-gold)" }}>{it.quote_number || "—"}</td>
                  <td>
                    <div className="text-sm font-medium">{it.client_name || "—"}</div>
                    <div className="text-[11px]" style={{ color: "var(--ax-text-muted)" }}>{it.client_phone || it.client_email || ""}</div>
                  </td>
                  <td className="text-sm">{it.project_type || "—"}</td>
                  <td><span className={QUOTE_CHIP[it.status] || "ax-chip"}>{it.status}</span></td>
                  <td className="text-sm ax-mono" style={{ color: "var(--ax-gold)" }}>{formatINR(it.grand_total || 0)}</td>
                  <td className="text-xs" style={{ color: "var(--ax-text-muted)" }}>{shortDate(it.updated_at || it.created_at)}</td>
                  <td>
                    <div className="flex items-center gap-1 justify-end">
                      <Link to={it.id} className="ax-btn ax-btn-ghost px-2" data-testid={`quote-edit-${it.id}`} title="Open"><Pencil className="w-3.5 h-3.5" /></Link>
                      <button type="button" className="ax-btn ax-btn-ghost ax-btn-danger px-2" onClick={() => onDelete(it)} data-testid={`quote-delete-${it.id}`} title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const EMPTY_QUOTE = {
  quote_number: "",
  client_name: "",
  client_phone: "",
  client_email: "",
  client_address: "",
  project_type: "",
  notes: "",
  rooms: [],
  discount_pct: 0,
  gst_pct: 18,
  installation_charges: 0,
  status: "draft",
  valid_until: "",
};

function newRoom(name = "Living Room") {
  return { id: cryptoRandom(), name, items: [] };
}
function newItem() {
  return { product_id: null, name: "", description: "", unit: "Nos", quantity: 1, unit_price: 0, image_url: "" };
}
function cryptoRandom() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return Math.random().toString(36).slice(2);
}

function computeTotals(rooms, discountPct, gstPct, install) {
  let subtotal = 0;
  for (const r of rooms) {
    for (const it of r.items) {
      subtotal += parseNumber(it.quantity) * parseNumber(it.unit_price);
    }
  }
  const discountAmount = +(subtotal * (parseNumber(discountPct) / 100)).toFixed(2);
  const afterDiscount = subtotal - discountAmount;
  const taxable = afterDiscount + parseNumber(install);
  const gstAmount = +(taxable * (parseNumber(gstPct) / 100)).toFixed(2);
  const grandTotal = +(taxable + gstAmount).toFixed(2);
  return { subtotal: +subtotal.toFixed(2), discountAmount, taxable: +taxable.toFixed(2), gstAmount, grandTotal };
}

function QuotationBuilder() {
  const { id } = useParams();
  const isEdit = !!id;
  const nav = useNavigate();
  const [quote, setQuote] = useState(EMPTY_QUOTE);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [products, setProducts] = useState([]);
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await adminApi.get("/products");
        setProducts(r.data?.products || []);
      } catch {
        /* ignore */
      }
      if (isEdit) {
        try {
          const r = await adminApi.get(`/quotations/${id}`);
          const q = r.data || EMPTY_QUOTE;
          // ensure rooms/items have ids
          const rooms = (q.rooms || []).map((rm) => ({ ...rm, id: rm.id || cryptoRandom() }));
          setQuote({ ...EMPTY_QUOTE, ...q, rooms });
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Quotation not found");
          nav("/admin-dashboard/quotations", { replace: true });
        } finally {
          setLoading(false);
        }
      }
    })();
  }, [id, isEdit, nav]);

  const totals = useMemo(
    () => computeTotals(quote.rooms, quote.discount_pct, quote.gst_pct, quote.installation_charges),
    [quote.rooms, quote.discount_pct, quote.gst_pct, quote.installation_charges]
  );

  const setField = (k, v) => setQuote((q) => ({ ...q, [k]: v }));

  const addRoom = () => setQuote((q) => ({ ...q, rooms: [...q.rooms, newRoom(`Room ${q.rooms.length + 1}`)] }));
  const removeRoom = (rid) => setQuote((q) => ({ ...q, rooms: q.rooms.filter((r) => r.id !== rid) }));
  const renameRoom = (rid, name) => setQuote((q) => ({ ...q, rooms: q.rooms.map((r) => (r.id === rid ? { ...r, name } : r)) }));

  const addItem = (rid) =>
    setQuote((q) => ({
      ...q,
      rooms: q.rooms.map((r) => (r.id === rid ? { ...r, items: [...r.items, newItem()] } : r)),
    }));
  const updateItem = (rid, idx, patch) =>
    setQuote((q) => ({
      ...q,
      rooms: q.rooms.map((r) => {
        if (r.id !== rid) return r;
        const items = r.items.map((it, i) => (i === idx ? { ...it, ...patch } : it));
        return { ...r, items };
      }),
    }));
  const removeItem = (rid, idx) =>
    setQuote((q) => ({
      ...q,
      rooms: q.rooms.map((r) => (r.id === rid ? { ...r, items: r.items.filter((_, i) => i !== idx) } : r)),
    }));

  const pickProductIntoItem = async (rid, idx, productId) => {
    const p = products.find((x) => x.id === productId);
    if (!p) return;
    updateItem(rid, idx, {
      product_id: p.id,
      name: p.name,
      description: p.description || "",
      unit: p.unit || "Nos",
      unit_price: p.price || 0,
      image_url: p.image_url || "",
    });
    if (!p.description) {
      try {
        const r = await adminApi.post("/ai/describe-product", { name: p.name, category: p.category, brand: p.brand });
        if (r.data?.description) {
          updateItem(rid, idx, { description: r.data.description });
        }
      } catch {
        /* silent fallback */
      }
    }
  };

  const aiFillItem = async (rid, idx, setBusy) => {
    const room = quote.rooms.find((r) => r.id === rid);
    const it = room?.items[idx];
    if (!it?.name?.trim()) {
      toast.error("Add a product name first");
      return;
    }
    setBusy && setBusy(true);
    try {
      const r = await adminApi.post("/ai/describe-product", { name: it.name });
      if (r.data?.description) updateItem(rid, idx, { description: r.data.description });
    } catch {
      /* silent */
    } finally {
      setBusy && setBusy(false);
    }
  };

  const save = async () => {
    if (!quote.client_name.trim()) {
      toast.error("Client name is required");
      return;
    }
    setSaving(true);
    // Ensure every line item has a description (silent backfill via AI)
    const rooms = JSON.parse(JSON.stringify(quote.rooms));
    for (const r of rooms) {
      for (const it of r.items) {
        if (it.name && !it.description) {
          try {
            const resp = await adminApi.post("/ai/describe-product", { name: it.name });
            it.description = resp.data?.description || "";
          } catch {
            /* silent fallback */
          }
        }
      }
    }
    const payload = {
      ...quote,
      rooms,
      discount_pct: parseNumber(quote.discount_pct),
      gst_pct: parseNumber(quote.gst_pct),
      installation_charges: parseNumber(quote.installation_charges),
    };
    try {
      let saved;
      if (isEdit) {
        const r = await adminApi.put(`/quotations/${id}`, payload);
        saved = r.data;
        toast.success("Quotation saved");
      } else {
        const r = await adminApi.post("/quotations", payload);
        saved = r.data;
        toast.success("Quotation created");
        nav(`/admin-dashboard/quotations/${saved.id}`, { replace: true });
      }
      setQuote((q) => ({ ...q, ...(saved || {}), rooms: (saved?.rooms || q.rooms).map((rm) => ({ ...rm, id: rm.id || cryptoRandom() })) }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const downloadPdf = async () => {
    if (!isEdit) {
      toast.message("Save the quotation first");
      return;
    }
    try {
      const token = getStoredToken();
      const res = await fetch(`${ADMIN_BASE}/quotations/${id}/pdf`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        credentials: "include",
      });
      if (!res.ok) throw new Error("PDF generation failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${quote.quote_number || "quotation"}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (e) {
      toast.error(e?.message || "Could not download PDF");
    }
  };

  const previewPdf = () => {
    if (!isEdit) {
      toast.message("Save first to preview the PDF");
      return;
    }
    const url = pdfUrl(id);
    window.open(url, "_blank", "noopener,noreferrer");
  };

  if (loading) {
    return (
      <div className="p-10 text-sm" style={{ color: "var(--ax-text-muted)" }} data-testid="quote-loading">
        Loading quotation…
      </div>
    );
  }

  return (
    <div className="p-10 max-w-[1320px] mx-auto" data-testid="quotation-builder">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <Link to="/admin-dashboard/quotations" className="ax-btn ax-btn-ghost" data-testid="quote-back">
          <ArrowLeft className="w-4 h-4" /> All quotations
        </Link>
        <div className="flex items-center gap-2 flex-wrap">
          <span className={QUOTE_CHIP[quote.status] || "ax-chip"}>{quote.status}</span>
          {quote.quote_number && (
            <span className="ax-chip ax-chip-gold ax-mono" data-testid="quote-number">{quote.quote_number}</span>
          )}
          <button type="button" className="ax-btn" onClick={previewPdf} disabled={!isEdit} data-testid="quote-preview">
            <Eye className="w-4 h-4" /> Preview PDF
          </button>
          <button type="button" className="ax-btn" onClick={downloadPdf} disabled={!isEdit} data-testid="quote-download">
            <Download className="w-4 h-4" /> Download
          </button>
          <button type="button" className="ax-btn" onClick={() => setShareOpen(true)} disabled={!isEdit} data-testid="quote-share">
            <Share2 className="w-4 h-4" /> Share
          </button>
          {isEdit && quote.status === "accepted" && (
            <button
              type="button"
              className="ax-btn"
              onClick={async () => {
                if (!window.confirm("Convert this accepted quotation into a project? This will mark the quotation as Converted.")) return;
                try {
                  const r = await adminApi.post(`/quotations/${id}/convert-to-project`);
                  toast.success("Project created");
                  setField("status", "converted");
                  if (r.data?.project?.id) {
                    nav(`/admin-dashboard/projects`);
                  }
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Convert failed");
                }
              }}
              data-testid="quote-convert"
            >
              <Briefcase className="w-4 h-4" /> Convert to project
            </button>
          )}
          <button type="button" className="ax-btn ax-btn-gold" onClick={save} disabled={saving} data-testid="quote-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? "Saving…" : "Save quotation"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-6">
        <div className="ax-glass p-5 lg:col-span-2">
          <div className="text-[10.5px] tracking-[0.32em] uppercase mb-3" style={{ color: "var(--ax-gold)" }}>
            Client & project
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="ax-label">Client name *</label>
              <input className="ax-input" name="client_name" value={quote.client_name} onChange={(e) => setField("client_name", e.target.value)} data-testid="quote-field-client" />
            </div>
            <div>
              <label className="ax-label">Project type</label>
              <input className="ax-input" placeholder="Villa, Penthouse…" value={quote.project_type || ""} onChange={(e) => setField("project_type", e.target.value)} data-testid="quote-field-project" />
            </div>
            <div>
              <label className="ax-label">Phone</label>
              <input className="ax-input" value={quote.client_phone || ""} onChange={(e) => setField("client_phone", e.target.value)} data-testid="quote-field-phone" />
            </div>
            <div>
              <label className="ax-label">Email</label>
              <input className="ax-input" value={quote.client_email || ""} onChange={(e) => setField("client_email", e.target.value)} data-testid="quote-field-email" />
            </div>
            <div className="md:col-span-2">
              <label className="ax-label">Site address</label>
              <input className="ax-input" value={quote.client_address || ""} onChange={(e) => setField("client_address", e.target.value)} data-testid="quote-field-address" />
            </div>
            <div>
              <label className="ax-label">Status</label>
              <select className="ax-select" value={quote.status} onChange={(e) => setField("status", e.target.value)} data-testid="quote-field-status">
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="ax-label">Valid until</label>
              <input className="ax-input" type="date" value={(quote.valid_until || "").slice(0, 10)} onChange={(e) => setField("valid_until", e.target.value)} data-testid="quote-field-valid" />
            </div>
            <div className="md:col-span-2">
              <label className="ax-label">Notes</label>
              <textarea className="ax-textarea" rows={2} value={quote.notes || ""} onChange={(e) => setField("notes", e.target.value)} data-testid="quote-field-notes" />
            </div>
          </div>
        </div>

        <div className="ax-glass p-5">
          <div className="text-[10.5px] tracking-[0.32em] uppercase mb-3" style={{ color: "var(--ax-gold)" }}>Totals</div>
          <div className="space-y-3">
            <Row label="Subtotal" value={formatINR(totals.subtotal)} />
            <FieldRow label="Discount %">
              <input className="ax-input ax-mono" style={{ width: 84, textAlign: "right" }} value={quote.discount_pct} onChange={(e) => setField("discount_pct", e.target.value)} data-testid="quote-discount" />
            </FieldRow>
            <Row label="Discount amount" value={`− ${formatINR(totals.discountAmount)}`} muted />
            <FieldRow label="Installation (₹)">
              <input className="ax-input ax-mono" style={{ width: 120, textAlign: "right" }} value={quote.installation_charges} onChange={(e) => setField("installation_charges", e.target.value)} data-testid="quote-install" />
            </FieldRow>
            <Row label="Taxable" value={formatINR(totals.taxable)} />
            <FieldRow label="GST %">
              <input className="ax-input ax-mono" style={{ width: 84, textAlign: "right" }} value={quote.gst_pct} onChange={(e) => setField("gst_pct", e.target.value)} data-testid="quote-gst" />
            </FieldRow>
            <Row label="GST amount" value={formatINR(totals.gstAmount)} muted />
            <div className="ax-hairline my-2" />
            <div className="flex items-center justify-between">
              <span className="text-xs tracking-[0.2em] uppercase" style={{ color: "var(--ax-text-dim)" }}>Grand total</span>
              <span className="ax-serif text-3xl" style={{ color: "var(--ax-gold)" }} data-testid="quote-grand-total">{formatINR(totals.grandTotal)}</span>
            </div>
          </div>
        </div>
      </div>

      <section className="space-y-5" data-testid="quote-rooms">
        {quote.rooms.length === 0 && (
          <div className="ax-glass p-10 text-center">
            <p className="text-sm mb-4" style={{ color: "var(--ax-text-dim)" }}>
              Add the first room to start composing this quotation. Each line uses the GenieX locked columns.
            </p>
            <button type="button" className="ax-btn ax-btn-gold" onClick={addRoom} data-testid="quote-add-first-room">
              <Plus className="w-4 h-4" /> Add room
            </button>
          </div>
        )}

        {quote.rooms.map((room, ridx) => (
          <RoomCard
            key={room.id}
            room={room}
            index={ridx}
            products={products}
            onRename={(name) => renameRoom(room.id, name)}
            onRemove={() => removeRoom(room.id)}
            onAddItem={() => addItem(room.id)}
            onUpdateItem={(idx, patch) => updateItem(room.id, idx, patch)}
            onRemoveItem={(idx) => removeItem(room.id, idx)}
            onPickProduct={(idx, pid) => pickProductIntoItem(room.id, idx, pid)}
            onAIFill={(idx, setBusy) => aiFillItem(room.id, idx, setBusy)}
          />
        ))}

        {quote.rooms.length > 0 && (
          <button type="button" className="ax-btn ax-btn-ghost" onClick={addRoom} data-testid="quote-add-room">
            <Plus className="w-4 h-4" /> Add another room
          </button>
        )}
      </section>

      {shareOpen && isEdit && (
        <ShareDialog
          quote={quote}
          onClose={() => setShareOpen(false)}
          onDownload={downloadPdf}
        />
      )}
    </div>
  );
}

function Row({ label, value, muted }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span style={{ color: "var(--ax-text-dim)" }}>{label}</span>
      <span className={muted ? "" : "ax-mono"} style={{ color: muted ? "var(--ax-text-muted)" : "var(--ax-text)" }}>
        {value}
      </span>
    </div>
  );
}
function FieldRow({ label, children }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm" style={{ color: "var(--ax-text-dim)" }}>{label}</span>
      {children}
    </div>
  );
}

function RoomCard({ room, index, products, onRename, onRemove, onAddItem, onUpdateItem, onRemoveItem, onPickProduct, onAIFill }) {
  return (
    <div className="ax-glass overflow-hidden" data-testid={`quote-room-${index}`}>
      <div className="flex items-center justify-between gap-4 px-5 py-4" style={{ borderBottom: "1px solid var(--ax-border)" }}>
        <div className="flex items-center gap-3 min-w-0">
          <GripVertical className="w-4 h-4 shrink-0" style={{ color: "var(--ax-text-muted)" }} />
          <input
            className="bg-transparent outline-none text-lg font-medium tracking-tight min-w-0 flex-1"
            value={room.name}
            onChange={(e) => onRename(e.target.value)}
            data-testid={`quote-room-name-${index}`}
          />
          <span className="ax-chip">{room.items.length} item{room.items.length === 1 ? "" : "s"}</span>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" className="ax-btn ax-btn-ghost" onClick={onAddItem} data-testid={`quote-add-item-${index}`}>
            <Plus className="w-4 h-4" /> Add line
          </button>
          <button type="button" className="ax-btn ax-btn-ghost ax-btn-danger" onClick={onRemove} data-testid={`quote-remove-room-${index}`}>
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="ax-scroll overflow-x-auto">
        <table className="ax-table" data-testid={`quote-items-${index}`}>
          <thead>
            <tr>
              <th style={{ width: 44 }}>#</th>
              <th style={{ width: 78 }}>Image</th>
              <th style={{ minWidth: 200 }}>Product Name</th>
              <th style={{ minWidth: 280 }}>What it does</th>
              <th style={{ width: 110 }}>Quantity</th>
              <th style={{ width: 140 }}>Unit Price</th>
              <th style={{ width: 160 }}>Amount</th>
              <th style={{ width: 50 }}></th>
            </tr>
          </thead>
          <tbody>
            {room.items.length === 0 && (
              <tr>
                <td colSpan={8} className="text-center text-sm py-8" style={{ color: "var(--ax-text-muted)" }}>
                  No line items yet. Add one to start.
                </td>
              </tr>
            )}
            {room.items.map((it, i) => {
              const amount = parseNumber(it.quantity) * parseNumber(it.unit_price);
              return (
                <LineRow
                  key={i}
                  room={room}
                  i={i}
                  index={index}
                  it={it}
                  amount={amount}
                  products={products}
                  onUpdateItem={onUpdateItem}
                  onRemoveItem={onRemoveItem}
                  onPickProduct={onPickProduct}
                  onAIFill={onAIFill}
                />
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function LineRow({ room, i, index, it, amount, products, onUpdateItem, onRemoveItem, onPickProduct, onAIFill }) {
  const [aiBusy, setAiBusy] = useState(false);
  return (
    <tr data-testid={`quote-line-${index}-${i}`}>
      <td className="text-xs ax-mono" style={{ color: "var(--ax-text-muted)" }}>{i + 1}</td>
      <td data-testid={`quote-line-image-${index}-${i}`}>
        {it.image_url ? (
          <img src={absoluteAdminUrl(it.image_url)} alt="" className="w-12 h-12 rounded-md object-cover" style={{ border: "1px solid var(--ax-border)" }} />
        ) : (
          <div className="w-12 h-12 rounded-md flex items-center justify-center" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid var(--ax-border)" }}>
            <Package className="w-3.5 h-3.5" style={{ color: "var(--ax-text-muted)" }} />
          </div>
        )}
      </td>
      <td>
        <input
          className="ax-input"
          placeholder="Type product name"
          value={it.name}
          onChange={(e) => onUpdateItem(i, { name: e.target.value })}
          data-testid={`quote-line-name-${index}-${i}`}
        />
        <select
          className="ax-select mt-1.5 text-[11px]"
          value={it.product_id || ""}
          onChange={(e) => onPickProduct(i, e.target.value)}
          data-testid={`quote-line-product-${index}-${i}`}
        >
          <option value="">— pick from catalog —</option>
          {products.map((p) => (
            <option key={p.id} value={p.id}>{p.name} · {p.brand || p.category}</option>
          ))}
        </select>
      </td>
      <td>
        <div className="relative">
          <textarea
            className="ax-textarea"
            rows={2}
            placeholder={aiBusy ? "AI is writing…" : "AI will write this"}
            value={it.description || ""}
            onChange={(e) => onUpdateItem(i, { description: e.target.value })}
            data-testid={`quote-line-desc-${index}-${i}`}
          />
          <button
            type="button"
            onClick={() => onAIFill(i, setAiBusy)}
            disabled={aiBusy || !it.name?.trim()}
            className="absolute top-1.5 right-1.5 text-[10px] flex items-center gap-1 px-2 py-1 rounded-md"
            style={{ background: "rgba(212,175,55,0.12)", color: "var(--ax-gold)", border: "1px solid rgba(212,175,55,0.3)" }}
            title="Regenerate via AI"
            data-testid={`quote-line-ai-${index}-${i}`}
          >
            {aiBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
            {aiBusy ? "…" : "AI"}
          </button>
        </div>
      </td>
      <td>
        <input
          className="ax-input ax-mono"
          style={{ textAlign: "right" }}
          value={it.quantity}
          onChange={(e) => onUpdateItem(i, { quantity: e.target.value })}
          data-testid={`quote-line-qty-${index}-${i}`}
        />
        <select
          className="ax-select mt-1.5 text-[11px]"
          value={it.unit || "Nos"}
          onChange={(e) => onUpdateItem(i, { unit: e.target.value })}
        >
          <option>Nos</option><option>Set</option><option>Pair</option><option>Meter</option><option>Sqft</option>
        </select>
      </td>
      <td>
        <input
          className="ax-input ax-mono"
          style={{ textAlign: "right" }}
          value={it.unit_price}
          onChange={(e) => onUpdateItem(i, { unit_price: e.target.value })}
          data-testid={`quote-line-price-${index}-${i}`}
        />
      </td>
      <td className="text-right ax-mono text-sm" style={{ color: "var(--ax-gold)" }} data-testid={`quote-line-amount-${index}-${i}`}>
        {formatINR(amount)}
      </td>
      <td>
        <button
          type="button"
          className="ax-btn ax-btn-ghost ax-btn-danger px-2"
          onClick={() => onRemoveItem(i)}
          data-testid={`quote-line-remove-${index}-${i}`}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </td>
    </tr>
  );
}

function ShareDialog({ quote, onClose, onDownload }) {
  const link = pdfUrl(quote.id);
  const fileRef = useRef(null);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success("PDF link copied to clipboard");
    } catch {
      // fallback: select the input
      fileRef.current?.select();
      document.execCommand && document.execCommand("copy");
      toast.success("PDF link copied");
    }
  };

  const shareWhatsApp = () => {
    const msg =
      `Hi ${quote.client_name || "there"}, your GenieX quotation ${quote.quote_number || ""} ` +
      `(₹ ${formatINR(quote.grand_total || 0).replace("₹", "")}) is ready.\nView PDF: ${link}`;
    const url = `https://wa.me/?text=${encodeURIComponent(msg)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(6px)" }}
      data-testid="quote-share-dialog"
    >
      <div className="ax-glass w-full max-w-md p-7 relative" style={{ borderRadius: 18 }}>
        <button type="button" className="absolute top-4 right-4 text-white/60 hover:text-white" onClick={onClose} data-testid="quote-share-close">
          <X className="w-4 h-4" />
        </button>
        <div className="mb-1 text-[10.5px] tracking-[0.32em] uppercase" style={{ color: "var(--ax-gold)" }}>Share</div>
        <h3 className="ax-serif text-2xl mb-5">Send the quotation</h3>

        <label className="ax-label">PDF link</label>
        <div className="flex items-center gap-2 mb-5">
          <input ref={fileRef} className="ax-input ax-mono text-[11px]" readOnly value={link} data-testid="quote-share-link" />
          <button type="button" className="ax-btn" onClick={copyLink} data-testid="quote-share-copy">
            <Copy className="w-4 h-4" /> Copy
          </button>
        </div>

        <div className="space-y-2">
          <button type="button" className="ax-btn w-full justify-center" onClick={shareWhatsApp} data-testid="quote-share-whatsapp">
            <Share2 className="w-4 h-4" /> Share via WhatsApp
          </button>
          <button type="button" className="ax-btn ax-btn-gold w-full justify-center" onClick={onDownload} data-testid="quote-share-download">
            <Download className="w-4 h-4" /> Download PDF
          </button>
        </div>

        <p className="text-[11px] mt-5 leading-relaxed" style={{ color: "var(--ax-text-muted)" }}>
          Tip: the PDF link requires admin auth. Use Download to save locally and share the file manually for maximum privacy.
        </p>
      </div>
    </div>
  );
}
